/* Category sale = current native filtered category membership ∩ current Sale membership.
 * Storefront GETs only. Intersect all pages before pagination; never fall back to the whole category. */
(function (root) {
  'use strict';
  const view = 'spike-sale';
  function handles(value, pageUrl, config) {
    const page = new URL(pageUrl), url = new URL(value, page);
    return config?.sale === true && url.origin === page.origin &&
      url.pathname === page.pathname && url.searchParams.get('view') === view;
  }
  function intersection(products, sale) {
    const ids = new Set(sale.map(p => p.id));
    return products.filter(p => ids.has(p.id));
  }
  function pageUrl(customer, page) {
    const url = new URL(customer);
    ['section_id', 'sections'].forEach(key => url.searchParams.delete(key));
    url.searchParams.set('view', view);
    url.searchParams.set('page', String(page));
    return `${url.pathname}${url.search}`;
  }
  function preserveLinks(doc, customer) {
    // Only collection controls, never product links or links to another category.
    doc.querySelectorAll('.js-facet-remove, .facets__reset, .collection-product-wrapper a, [data-spike-sale-ready] a').forEach(link => {
      const url = new URL(link.getAttribute('href'), customer);
      if (url.origin !== customer.origin || url.pathname !== customer.pathname) return;
      url.searchParams.set('view', view);
      for (const key of ['preview_theme_id', '_ab', '_fd', '_sc']) {
        if (customer.searchParams.has(key)) url.searchParams.set(key, customer.searchParams.get(key));
      }
      link.setAttribute('href', `${url.pathname}${url.search}`);
    });
  }
  function pagination(doc, customer, meta, settings) {
    const pages = Math.ceil(meta.count / meta.pageSize);
    if (pages < 2) return null;
    const fragment = doc.getElementById('spike-sale-pagination').content.cloneNode(true);
    const start = Math.min((meta.page - 1) * meta.pageSize + 1, meta.count);
    const end = Math.min(meta.page * meta.pageSize, meta.count);
    fragment.querySelector('[data-total-start]').textContent = start;
    fragment.querySelector('[data-total-end]').textContent = end;
    const total = fragment.querySelector('[data-sale-total]');
    total.textContent = total.textContent.replace('__COUNT__', meta.count);
    fragment.querySelector('.pagination-total-item').style.width = `${end / meta.count * 100}%`;
    const list = fragment.querySelector('ul');
    if (['infinite', 'show_more'].includes(settings.mode)) {
      const wrap = doc.createElement('div'); wrap.className = 'pagination-page-item pagination-page-infinite';
      if (meta.page < pages) {
        const link = doc.createElement('a'); link.className = 'button button--secondary';
        link.href = pageUrl(customer, meta.page + 1); link.dataset.href = link.getAttribute('href');
        link.dataset.infiniteScrolling = ''; link.dataset.loadMore = settings.more; link.dataset.loadingMore = settings.more;
        link.textContent = settings.more; wrap.append(link);
      }
      list.replaceWith(wrap);
    } else {
      const add = (page, text, arrow = '') => {
        const li = doc.createElement('li'); li.className = arrow ? 'pagination-arrow' : 'pagination-num';
        const link = doc.createElement(page === meta.page || page == null ? 'span' : 'a');
        link.className = `pagination__item ${arrow ? `pagination__item--${arrow} pagination__item-arrow link` : 'link'}`;
        if (page === meta.page) { link.classList.add('pagination__item--current'); link.setAttribute('aria-current', 'page'); }
        else if (page != null) link.setAttribute('href', pageUrl(customer, page));
        link.textContent = text; li.append(link); list.append(li);
      };
      if (meta.page > 1) add(meta.page - 1, settings.previous, 'prev');
      const numbers = new Set([1, pages]);
      for (let p = Math.max(1, meta.page - 2); p <= Math.min(pages, meta.page + 2); p++) numbers.add(p);
      let previous = 0;
      for (const p of [...numbers].sort((a, b) => a - b)) {
        if (previous && p - previous > 1) add(null, '…');
        add(p, String(p)); previous = p;
      }
      if (meta.page < pages) add(meta.page + 1, settings.next, 'next');
    }
    return fragment;
  }
  async function load(value, html, config, options = {}) {
    const customer = new URL(value), preview = root.SpikeCategoryPreview;
    if (!handles(customer, root.location.href, config)) throw new Error('Invalid category sale request');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const meta = JSON.parse(doc.querySelector('[data-spike-category-page]')?.textContent);
    const settings = JSON.parse(doc.getElementById('spike-sale-settings')?.textContent);
    if (meta?.handle !== config.source || !Number.isInteger(meta.count) || meta.count < 0 || meta.count > 5000 ||
      !Number.isInteger(meta.pageSize) || meta.pageSize < 1 || meta.pageSize > 250) throw new Error('Invalid category sale response');
    const grid = doc.querySelector('#main-collection-product-grid.productListing');
    const collection = grid?.closest('.collection');
    if (!collection) throw new Error('Missing category sale grid');
    const saleUrl = new URL(customer);
    saleUrl.pathname = `${preview.collectionPath(customer).locale}/collections/vyprodej`;
    // Filters belong to the category. Read ALL Sale membership, then intersect by product ID.
    [...saleUrl.searchParams.keys()].filter(key => key.startsWith('filter.')).forEach(key => saleUrl.searchParams.delete(key));
    const ranked = customer.searchParams.get('sort_by') === 'best-selling' && preview.valid(config, customer);
    const [products, sale] = await Promise.all([
      preview.members(customer, config, meta.count, options, ranked ? 'title-ascending' : customer.searchParams.get('sort_by')),
      preview.members(saleUrl, { source: 'vyprodej' }, null, options),
    ]);
    const eligible = intersection(products, sale);
    meta.count = eligible.length;
    meta.page = Number(customer.searchParams.get('page') || 1);
    if (!Number.isInteger(meta.page) || meta.page < 1) throw new Error('Invalid sale page');
    const selected = ranked ? preview.rankedPage(eligible, config.preview, meta.page, meta.pageSize) :
      eligible.slice((meta.page - 1) * meta.pageSize, meta.page * meta.pageSize);
    collection.replaceChildren(grid);
    grid.innerHTML = await preview.cards(customer, config, selected, meta, grid, options);
    grid.dataset.spikeSaleReady = '';
    grid.dataset.spikeSaleCount = String(meta.count);
    if (!selected.length) collection.append(doc.getElementById('spike-sale-empty').content.cloneNode(true));
    const nav = pagination(doc, customer, meta, settings);
    if (nav) collection.append(nav);
    doc.querySelectorAll('.results-count .count').forEach(node => { node.textContent = meta.count; });
    doc.querySelector('[data-spike-category-page]').textContent = JSON.stringify(meta);
    preserveLinks(doc, customer);
    return doc.documentElement.outerHTML;
  }
  const isSale = () => root?.document.getElementById('spike-sale-settings');
  function pending() {
    if (!isSale()) return;
    root.document.documentElement.classList.remove('spike-sale-ready', 'spike-sale-failed');
  }
  function sync() {
    if (!isSale()) return;
    root.document.documentElement.classList.toggle('spike-sale-ready', !!root.document.querySelector('#CollectionProductGrid [data-spike-sale-ready]'));
  }
  function failed(message) {
    if (!isSale()) return;
    root.document.documentElement.classList.add('spike-sale-failed');
    const button = root.document.createElement('button'); button.className = 'button button--secondary';
    button.textContent = JSON.parse(isSale().textContent).retry;
    button.addEventListener('click', () => root.location.reload()); message.append(button);
  }
  const api = { handles, intersection, pageUrl, load, pending, sync, failed };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root?.document) {
    root.SpikeCategorySale = api;
    // Load-more uses the theme's append path instead of renderProductGrid.
    // Keep its new range summary in the toolbar as well.
    const watch = () => {
      const grid = root.document.getElementById('CollectionProductGrid');
      if (!isSale() || !grid) return;
      new MutationObserver(() => {
        const summary = grid.querySelector('.collection .pagination-page-total');
        const toolbar = root.document.querySelector('.toolbar-limitView');
        if (!summary || !toolbar) return;
        const previous = toolbar.querySelector('.pagination-page-total');
        if (previous) previous.replaceWith(summary);
        else toolbar.append(summary);
      }).observe(grid, { childList: true, subtree: true });
    };
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', watch, { once: true });
    else watch();
  }
})(typeof window === 'undefined' ? null : window);
